/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ScheduleTaskCcResourceListItemDto,
  ScheduleTaskChangeDateAfterCompleteType,
  ScheduleTaskDetailsDto,
  ScheduleTaskNoteListItemDto,
  ScheduleTaskRelatedResourceTaskListItemDto,
  ScheduleTaskTodoEventType,
  ScheduleTaskTodoListItemDto,
  ScheduleTaskType,
  ScheduleTaskUserResourceListItemDto,
  ScheduleType,
  TaskRelationDto,
  UserListFilterDto,
  UserListItemDto,
  UserSystemRole,
} from '@gh/builder-shared-data';
import {
  createArrayHelper,
  DateTimeTypes,
  DictionaryItem,
  EntityConfig,
  entityValidatorByEntityConfig,
  EnumDictionary,
  FormStateSnapshot,
  formStateSnapshot,
  getFormStateSnapshotValue,
} from '@gh/common-shared';
import { EMPTY_ID, ID, WithUniqueId, WithVersion } from '@gh/common-shared-data';
import { FormMode, validationError } from '@gh/core-ui';
import { currentDate, currentNormalizedDate, normalizeDate, sortByBoolField } from '@gh/core-util';

import { includes, isNil, map, omit, pick, uniqueId } from 'lodash';

export type ScheduleTaskTodoListItem = WithUniqueId<ScheduleTaskTodoListItemDto>;
export type ScheduleTaskNoteListItem = WithUniqueId<ScheduleTaskNoteListItemDto>;
export type ScheduleTaskCcResourceListItem = ScheduleTaskCcResourceListItemDto;
export type ScheduleTaskUserResourceListItem = ScheduleTaskUserResourceListItemDto;
export type ScheduleTaskRelatedResourceTaskListItem = WithUniqueId<ScheduleTaskRelatedResourceTaskListItemDto>;
export type ScheduleTaskUserAutocompleteFilter = UserListFilterDto;
export type ScheduleTaskUserAutocomplete = UserListItemDto;

export const DEFAULT_ADD_TODO_DAYS_CONTROL_VALUE: number = 2;

export type ScheduleTaskInfo = {
  name?: string;
  description?: string;
  startDate: Date;
  endDate?: Date;
  confirmedOnDate?: Date;
  confirmedForDate?: Date;
  resource?: string;
  duration?: number;
  completionPct?: number;
  isExcludeFromGanttChart: boolean;
  orderNumber?: number;
  costCenterCode?: number;
  confirmedWith?: string;
  changeDateAfterComplete?: ScheduleTaskChangeDateAfterCompleteType;
  scheduleType: ScheduleType;
};

export enum ScheduleTaskDetailsTabName {
  General = 'GENERAL',
  Notes = 'NOTES',
  Todos = 'TODOS',
}

export enum ScheduleTaskDetailsGeneralSubtabName {
  CcResource = 'CC_RESOURCE',
  UserResource = 'USER_RESOURCE',
}

export type WithTab<T> = T & { selectedTab: ScheduleTaskDetailsTabName };
export type WithGeneralSubtab<T> = T & { selectedGeneralSubtab?: ScheduleTaskDetailsGeneralSubtabName };

export type ScheduleTaskDetails = WithTab<WithGeneralSubtab<WithVersion<{
  info: FormStateSnapshot<ScheduleTaskInfo>;
  currentTasks: ScheduleTaskRelatedResourceTaskListItem[];
  baseStartDate?: Date;
  baseEndDate?: Date;
  scheduleTaskId: ID;
  type: ScheduleTaskType;
  todo: ScheduleTaskTodoListItem[];
  notes: ScheduleTaskNoteListItem[];
  parentTaskNumber?: number;
  taskNumber?: number;
  costCenterId?: ID;
  jobOrderId?: ID;
  predecessors: TaskRelationDto[];
  completionDate?: Date;
  completionCode?: number;
  completionPct?: number;
  supplierCostCenterId?: ID;
  supplierContactId?: ID;
  userId?: ID;
  ccResources: ScheduleTaskCcResourceListItem[];
  employeeResources: ScheduleTaskUserResourceListItem[];
  isSalesFolderActive: boolean;
  isFinalized: boolean;
  isNotProceeding: boolean;
  isFromCriticalPath: boolean;
  isIncludedTasksExist: boolean;
  isInSupervisorSchedule: boolean;
  isSlab: boolean;
  isContractStart: boolean;
  jobNumber: string;
  salesFolderName: string;
  isBaseDatesSavedBefore?: boolean;
}>>>;

export const isExistsCcResourceGeneralSubtab = (type: ScheduleTaskType, isIncludedTasksExist: boolean): boolean =>
  type === ScheduleTaskType.CostCenter || type === ScheduleTaskType.Empty
  || ((type === ScheduleTaskType.CriticalPath || type === ScheduleTaskType.Summary) && !isIncludedTasksExist);

export const isExistsUserResourceGeneralSubtab = (type: ScheduleTaskType, isIncludedTasksExist: boolean): boolean =>
  type === ScheduleTaskType.CostCenter ||  type === ScheduleTaskType.Empty
  || ((type === ScheduleTaskType.CriticalPath || type === ScheduleTaskType.Summary) && !isIncludedTasksExist);

export const SCHEDULE_TASK_DETAILS_INIT: ScheduleTaskDetails = {
  info: formStateSnapshot({
    name: void 0,
    description: void 0,
    startDate: currentDate(),
    endDate: void 0,
    confirmedOnDate: currentDate(),
    confirmedForDate: void 0,
    resource: void 0,
    duration: void 0,
    completionPct: void 0,
    isExcludeFromGanttChart: false,
    orderNumber: void 0,
    costCenterCode: void 0,
    confirmedWith: void 0,
    changeDateAfterComplete: void 0,
    scheduleType: ScheduleType.Construction,
  }),
  currentTasks: [],
  baseStartDate: void 0,
  baseEndDate: void 0,
  selectedTab: <any>void 0,
  selectedGeneralSubtab: void 0,
  scheduleTaskId: EMPTY_ID,
  type: ScheduleTaskType.Empty,
  todo: [],
  notes: [],
  taskNumber: void 0,
  parentTaskNumber: void 0,
  costCenterId: void 0,
  jobOrderId: void 0,
  predecessors: [],
  completionDate: void 0,
  completionCode: void 0,
  supplierContactId: void 0,
  userId: void 0,
  ccResources: [],
  employeeResources: [],
  isSalesFolderActive: false,
  isFinalized: false,
  isNotProceeding: false,
  isFromCriticalPath: false,
  isIncludedTasksExist: false,
  isInSupervisorSchedule: false,
  isSlab: false,
  isContractStart: false,
  jobNumber: '',
  salesFolderName: '',
};

export const scheduleTaskDetailsFromDto = (scheduleType: ScheduleType,
                                           dto: ScheduleTaskDetailsDto) => <ScheduleTaskDetails>({
  info: formStateSnapshot(<ScheduleTaskInfo>{
    ...pick(dto, [
      'name',
      'description',
      'startDate',
      'endDate',
      'confirmedOnDate',
      'confirmedForDate',
      'resource',
      'duration',
      'completionPct',
      'isExcludeFromGanttChart',
      'orderNumber',
      'costCenterCode',
      'confirmedWith',
      'changeDateAfterComplete',
    ]),
    scheduleType,
  }),
  scheduleTaskId: dto.scheduleTaskId,
  baseStartDate: dto.baseStartDate,
  baseEndDate: dto.baseEndDate,
  currentTasks: [],
  selectedTab: ScheduleTaskDetailsTabName.General,
  selectedGeneralSubtab: isExistsCcResourceGeneralSubtab(dto.type, dto.isIncludedTasksExist)
    ? ScheduleTaskDetailsGeneralSubtabName.CcResource
    : isExistsUserResourceGeneralSubtab(dto.type, dto.isIncludedTasksExist)
      ? ScheduleTaskDetailsGeneralSubtabName.UserResource
      : void 0,
  type: dto.type,
  todo: map(dto.todo, (todo: ScheduleTaskTodoListItem) =>
    ({ ...todo, uniqueId: generateScheduleTaskTodoId() })),
  notes: map(dto.notes, (note: ScheduleTaskNoteListItem) =>
    ({ ...note, uniqueId: generateScheduleTaskNoteId() })),
  taskNumber: dto.taskNumber,
  parentTaskNumber: dto.parentTaskNumber,
  costCenterId: dto.costCenterId,
  jobOrderId: dto.jobOrderId,
  predecessors: dto.predecessors,
  completionDate: dto.completionDate,
  completionCode: dto.completionCode,
  supplierContactId: dto.supplierContactId,
  userId: dto.employeeContactId,
  ccResources: sortByBoolField(dto.ccResources || [], 'isPreferred', 'desc'),
  employeeResources: dto.employeeResources,
  isSalesFolderActive: dto.isSalesFolderActive,
  isFinalized: dto.isFinalized,
  isNotProceeding: dto.isNotProceeding,
  isFromCriticalPath: dto.isFromCriticalPath,
  isBaseDatesSavedBefore: dto.isBaseDatesSavedBefore,
  isIncludedTasksExist: dto.isIncludedTasksExist,
  isInSupervisorSchedule: dto.isInSupervisorSchedule,
  isSlab: dto.isSlab,
  isContractStart: dto.isContractStart,
  jobNumber: dto.jobNumber,
  salesFolderName: dto.salesFolderName,
  version: dto.version,
});

export const scheduleTaskDetailsToDto = (details: ScheduleTaskDetails) => {
  const info = getFormStateSnapshotValue(details.info);
  return <ScheduleTaskDetailsDto>{
    ...omit(info, 'scheduleType'),
    ...omit(details, [
      'info', 'currentTasks', 'selectedTab', 'selectedGeneralSubtab',
      'ccResources', 'userResources', 'supplierCostCenterId', 'userId',
    ]),
    employeeContactId: details.userId,
    predecessors: info.isExcludeFromGanttChart ? [] : details.predecessors,
    todo: map(details.todo, (todo: any) => omit(todo, ['uniqueId'])),
    notes: map(details.notes, (note: any) => omit(note, ['uniqueId'])),
  };
};

export const getScheduleTaskDefaultGeneralSubTab = (taskDetails: ScheduleTaskDetails) => ({
  [ScheduleTaskType.CriticalPath]: !taskDetails.isIncludedTasksExist
    ? ScheduleTaskDetailsGeneralSubtabName.CcResource
    : void 0,
  [ScheduleTaskType.CostCenter]: ScheduleTaskDetailsGeneralSubtabName.CcResource,
  [ScheduleTaskType.Summary]: !taskDetails.isIncludedTasksExist
    ? ScheduleTaskDetailsGeneralSubtabName.CcResource
    : void 0,
  [ScheduleTaskType.Empty]: ScheduleTaskDetailsGeneralSubtabName.CcResource,
}[taskDetails.type]);

export const getScheduleTaskNoteId = ({ uniqueId: id }: WithUniqueId<ScheduleTaskNoteListItem>) => id;
export const scheduleTaskNoteArr = createArrayHelper(getScheduleTaskNoteId);
export const generateScheduleTaskNoteId = () => uniqueId('schedule-task-note:');
export const createTaskNote = (memo?: string, addedBy?: string): WithUniqueId<ScheduleTaskNoteListItem> => ({
  scheduleTaskNoteId: EMPTY_ID,
  uniqueId: generateScheduleTaskNoteId(),
  memo,
  addedDate: currentDate(),
  addedBy,
});

export const getScheduleTaskTodoId = ({ uniqueId: id }: WithUniqueId<ScheduleTaskTodoListItem>) => id;
export const scheduleTaskTodoArr = createArrayHelper(getScheduleTaskTodoId);
export const generateScheduleTaskTodoId = () => uniqueId('schedule-task-todo:');
export const createTaskTodo = ({ days, event, systemRole, user, memo }: ScheduleTaskTodoListItem):
  WithUniqueId<ScheduleTaskTodoListItem> => ({
  scheduleTaskTodoId: EMPTY_ID,
  uniqueId: generateScheduleTaskTodoId(),
  days,
  memo,
  event,
  user,
  systemRole,
  addedDate: currentDate(),
});

export const generateScheduleTaskCurrentTaskListItemId = () => uniqueId('schedule-task-current-task:');
export const createTaskCurrentTaskListItem = ({ name, completionPct, duration, endDate, scheduleFor, startDate, type }:
                                                ScheduleTaskRelatedResourceTaskListItem):
  ScheduleTaskRelatedResourceTaskListItem => ({
  uniqueId: generateScheduleTaskCurrentTaskListItemId(),
  name,
  completionPct,
  duration,
  endDate,
  scheduleFor,
  startDate,
  type,
});

export type ScheduleTaskTodoDialogConfig = {
  scheduleType: ScheduleType;
  scheduleTaskTodoId?: ID;
  days?: number;
  event: ScheduleTaskTodoEventType;
  systemRole: UserSystemRole;
  userId?: ID;
  memo?: string;
  mode: FormMode;
};

export const scheduleTaskInfoEntityConfig = (currentConfirmedForDate: Maybe<Date>,
                                             currentConfirmedOnDate: Maybe<Date>): EntityConfig<ScheduleTaskInfo> => ({
  name: { required: true },
  confirmedForDate: {
    type: DateTimeTypes.ShortDate,
    validator: ({ scheduleType }: ScheduleTaskInfo, _2: string, date: Date) =>
      scheduleType === ScheduleType.Construction
        ? isNil(date) || (normalizeDate(date) >= currentNormalizedDate()
          || (currentConfirmedForDate && normalizeDate(date) >= normalizeDate(currentConfirmedForDate)))
          ? void 0
          : { 'confirmedForDate': validationError('msg_field_invalid_format') }
        : void 0,
  },
  confirmedOnDate: {
    type: DateTimeTypes.ShortDate,
    validator: ({ scheduleType, confirmedOnDate }: ScheduleTaskInfo, _2: string, date: Date) =>
      scheduleType === ScheduleType.Construction
        ? isNil(date) || (normalizeDate(date) >= currentNormalizedDate()
          || (currentConfirmedOnDate && normalizeDate(date) >= normalizeDate(currentConfirmedOnDate)))
          ? void 0
          : { 'confirmedOnDate': validationError('msg_field_invalid_format') }
        : void 0,
  },
});

export const validateScheduleTaskInfo = (currentConfirmedForDate: Maybe<Date>,
                                         currentConfirmedOnDate: Maybe<Date>) =>
  entityValidatorByEntityConfig(scheduleTaskInfoEntityConfig(currentConfirmedForDate, currentConfirmedOnDate));

export const filterSystemRolesDictionary = (systemRoleList: UserSystemRole[]) =>
  (dictionary: EnumDictionary<UserSystemRole>) =>
    dictionary.filter(({ id: role }: DictionaryItem<any>) => includes(systemRoleList, role));
